import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { transfersService } from '../services/transfersService';
import { driversService } from '../services/driversService';
import { providersService } from '../services/providersService';
import api from '../services/api';
import { Transfer, TransferFilters, TransferStatus, UserRole, TripType, Driver, Provider, VehicleType, Currency } from '../types';
import { format } from 'date-fns';
import { RootState } from '../store/store';
import { TransferDetailModal } from '../components/TransferDetailModal';
import { ExcelImportModal } from '../components/ExcelImportModal';
import { CalendarPage } from './CalendarPage';

const FILTERS_STORAGE_KEY = 'transfers_filters_v2';
const SEARCH_ALL_STORAGE_KEY = 'transfers_search_all';
const getDefaultFilters = (): TransferFilters => ({ activeOnly: true });
const hasNonDefaultFilters = (filters: TransferFilters): boolean => {
  // Show "Filtreleri Temizle" when filters deviate from the default (activeOnly:true, no other filter)
  const { activeOnly, ...rest } = filters;
  return Object.values(rest).some(Boolean) || activeOnly !== true;
};
const loadSavedFilters = (): TransferFilters => {
  try {
    const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return getDefaultFilters();
};

export const TransfersPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFiltersRaw] = useState<TransferFilters>(loadSavedFilters());
  const setFilters = (newFilters: TransferFilters) => {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(newFilters));
    setFiltersRaw(newFilters);
  };
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [matchStatusFilter, setMatchStatusFilter] = useState<'all' | 'matched' | 'unmatched'>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [searchAll, setSearchAllRaw] = useState(() => {
    try { return sessionStorage.getItem(SEARCH_ALL_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const setSearchAll = (val: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof val === 'function' ? val(searchAll) : val;
    sessionStorage.setItem(SEARCH_ALL_STORAGE_KEY, String(next));
    setSearchAllRaw(next);
  };
  const [viewMode, setViewModeRaw] = useState<'list' | 'calendar'>(() => {
    try { return (sessionStorage.getItem('transfers_view_mode') as 'list' | 'calendar') || 'list'; } catch { return 'list'; }
  });
  const setViewMode = (mode: 'list' | 'calendar') => {
    sessionStorage.setItem('transfers_view_mode', mode);
    setViewModeRaw(mode);
  };

  // Check granular permissions for STAFF
  const canAddTransfer = user?.role === UserRole.MANAGER ||
                         user?.role === UserRole.ORG_ADMIN ||
                         user?.role === UserRole.PLATFORM_ADMIN ||
                         (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('add'));

  const canEditTransfer = user?.role === UserRole.MANAGER ||
                          user?.role === UserRole.ORG_ADMIN ||
                          user?.role === UserRole.PLATFORM_ADMIN ||
                          (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('edit'));

  const canDeleteTransfer = user?.role === UserRole.MANAGER ||
                            user?.role === UserRole.ORG_ADMIN ||
                            user?.role === UserRole.PLATFORM_ADMIN ||
                            (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('delete'));

  const canBulkImport = user?.role === UserRole.MANAGER ||
                         user?.role === UserRole.ORG_ADMIN ||
                         user?.role === UserRole.PLATFORM_ADMIN;

  const canViewFinancials = user?.role === UserRole.MANAGER ||
                            user?.role === UserRole.ORG_ADMIN ||
                            user?.role === UserRole.PLATFORM_ADMIN;

  const currencyLabel = (currency?: Currency | string) => {
    switch (currency) {
      case Currency.EUR: return '€';
      case Currency.USD: return '$';
      case Currency.GBP: return '£';
      case Currency.TRY: return 'TL';
      default: return 'TL';
    }
  };

  // Load dropdown data on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [driversData, providersData, vehicleTypesData] = await Promise.all([
          driversService.getAll(true),
          providersService.getAll(true),
          api.get('/vehicle-types?activeOnly=true').then(res => res.data),
        ]);

        setDrivers(driversData);
        setProviders(providersData);
        setVehicleTypes(vehicleTypesData);
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
      }
    };
    loadDropdownData();
  }, [user]);

  useEffect(() => {
    loadTransfers();
  }, [filters, searchAll]);

  // Handle URL parameter for viewing transfer from notification
  useEffect(() => {
    const viewTransferId = searchParams.get('view');
    if (viewTransferId) {
      // Clear the URL parameter immediately to prevent re-triggering
      setSearchParams({}, { replace: true });

      // Fetch the transfer and open modal
      transfersService.getById(viewTransferId)
        .then(fetchedTransfer => {
          setSelectedTransfer(fetchedTransfer);
          setIsDetailModalOpen(true);
        })
        .catch(err => {
          console.error('Failed to load transfer for viewing:', err);
          toast.error(t('transfers.failedToLoad'));
        });
    }
  }, [searchParams, setSearchParams, t]);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const effectiveFilters: TransferFilters = (searchAll && filters.search)
        ? { search: filters.search }
        : filters;
      const result = await transfersService.getAll(effectiveFilters);
      setTransfers(result.data);
      setTotalTransfers(result.total);
    } catch (error: any) {
      toast.error(t('transfers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Apply match status filter and sorting to transfers
  const sortedTransfers = React.useMemo(() => {
    // First filter by match status
    let filtered = [...transfers];
    if (matchStatusFilter === 'matched') {
      filtered = filtered.filter(t => t.matchedTransferId);
    } else if (matchStatusFilter === 'unmatched') {
      filtered = filtered.filter(t => !t.matchedTransferId);
    }

    // Filter by assignment status
    if (assignmentFilter === 'assigned') {
      filtered = filtered.filter(t => t.providerDriverName || t.assignedDriver);
    } else if (assignmentFilter === 'unassigned') {
      filtered = filtered.filter(t => !t.providerDriverName && !t.assignedDriver);
    }

    const sorted = filtered;

    sorted.sort((a, b) => {
      let compareResult = 0;

      switch (sortBy) {
        case 'date':
          const dateA = new Date(a.transferDate + 'T' + a.transferTime);
          const dateB = new Date(b.transferDate + 'T' + b.transferTime);
          compareResult = dateA.getTime() - dateB.getTime();
          break;

        case 'customer':
          compareResult = (a.customerName || '').localeCompare(b.customerName || '');
          break;

        case 'status':
          compareResult = a.status.localeCompare(b.status);
          break;

        case 'price':
          compareResult = (a.providerPrice || 0) - (b.providerPrice || 0);
          break;

        case 'pickup':
          compareResult = a.pickupLocation.localeCompare(b.pickupLocation);
          break;

        case 'assigned':
          const assignedA = a.providerDriverName || a.assignedDriver?.fullName || '';
          const assignedB = b.providerDriverName || b.assignedDriver?.fullName || '';
          compareResult = assignedA.localeCompare(assignedB);
          break;

        case 'plate':
          const plateA = a.providerPlate || '';
          const plateB = b.providerPlate || '';
          compareResult = plateA.localeCompare(plateB);
          break;

        default:
          compareResult = 0;
      }

      return sortOrder === 'asc' ? compareResult : -compareResult;
    });

    return sorted;
  }, [transfers, sortBy, sortOrder, matchStatusFilter, assignmentFilter]);

  const nonCancelledCount = sortedTransfers.filter(t => t.status !== TransferStatus.CANCELLED).length;
  const cancelledCount = sortedTransfers.length - nonCancelledCount;

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('transfers.confirmDelete'))) {
      return;
    }

    try {
      await transfersService.delete(id);
      toast.success(t('transfers.transferDeleted'));
      loadTransfers();
    } catch (error: any) {
      toast.error(t('transfers.failedToDelete'));
    }
  };

  const handleExportToExcel = async () => {
    try {
      const queryParams = new URLSearchParams();

      // Add filters to query params
      if (filters.status) queryParams.append('status', filters.status);
      if (!filters.status && filters.activeOnly) queryParams.append('activeOnly', 'true');
      else if (!filters.status && filters.excludeStatus) queryParams.append('excludeStatus', filters.excludeStatus);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      if (filters.tripType) queryParams.append('tripType', filters.tripType);
      if (filters.vehicleType) queryParams.append('vehicleType', filters.vehicleType);
      if (filters.assignedDriverId) queryParams.append('assignedDriverId', filters.assignedDriverId);
      if (filters.assignedProviderId) queryParams.append('assignedProviderId', filters.assignedProviderId);

      // Get token from localStorage
      const token = localStorage.getItem('accessToken');

      // Fetch Excel file
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiBaseUrl}/transfers/export?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transfers_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('transfers.exportSuccess'));
    } catch (error: any) {
      toast.error(t('transfers.exportFailed'));
      console.error('Export error:', error);
    }
  };

  const handleExportFinancialReport = async () => {
    try {
      const queryParams = new URLSearchParams();

      // Add filters to query params
      if (filters.status) queryParams.append('status', filters.status);
      if (!filters.status && filters.activeOnly) queryParams.append('activeOnly', 'true');
      else if (!filters.status && filters.excludeStatus) queryParams.append('excludeStatus', filters.excludeStatus);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      if (filters.tripType) queryParams.append('tripType', filters.tripType);
      if (filters.vehicleType) queryParams.append('vehicleType', filters.vehicleType);
      if (filters.assignedDriverId) queryParams.append('assignedDriverId', filters.assignedDriverId);
      if (filters.assignedProviderId) queryParams.append('assignedProviderId', filters.assignedProviderId);

      // Get token from localStorage
      const token = localStorage.getItem('accessToken');

      // Fetch Financial Report Excel file
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiBaseUrl}/transfers/financial-report?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export financial report');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financial_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('transfers.financialReportExported'));
    } catch (error: any) {
      toast.error(t('transfers.financialReportFailed'));
      console.error('Export error:', error);
    }
  };

  // Provider users see compact table view
  if (user?.role === UserRole.PROVIDER_USER) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('transfers.myTransfers')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{t('transfers.clickToViewDetails')}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t('transfers.startDate')}</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t('transfers.endDate')}</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t('common.status')}</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as TransferStatus || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('transfers.allStatus')}</option>
                <option value={TransferStatus.AVAILABLE}>{t('transfers.statuses.available')}</option>
                <option value={TransferStatus.CONFIRMED}>{t('transfers.statuses.confirmed')}</option>
                <option value={TransferStatus.IN_PROGRESS}>{t('transfers.statuses.inProgress')}</option>
                <option value={TransferStatus.COMPLETED}>{t('transfers.statuses.completed')}</option>
                <option value={TransferStatus.CANCELLED}>{t('transfers.statuses.cancelled')}</option>
              </select>
            </div>
          </div>
          {hasNonDefaultFilters(filters) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setFilters(getDefaultFilters())}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('transfers.clearFilters')}
              </button>
            </div>
          )}
        </div>

        {/* Transfer Count */}
        {!loading && sortedTransfers.length > 0 && (
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-200">{nonCancelledCount}</span>
            <span>transfer</span>
            {cancelledCount > 0 && (
              <span className="text-gray-400 dark:text-gray-500">({cancelledCount} iptal hariç)</span>
            )}
          </div>
        )}

        {/* Compact Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-600">{t('common.loading')}</div>
          ) : transfers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>{t('transfers.noTransfersFound')}</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="sm:hidden divide-y divide-gray-200">
                {transfers.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => { setSelectedTransfer(transfer); setIsDetailModalOpen(true); }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${transfer.tripType === 'arrival' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {getTripTypeLabel(transfer.tripType, t)}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transfer.status)}`}>
                          {transfer.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                        {format(new Date(transfer.transferDate), 'dd.MM.yyyy')} {transfer.transferTime.substring(0, 5)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-900 mb-1">
                      {transfer.pickupLocation} <span className="text-gray-400">→</span> {transfer.dropoffLocation}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                      {transfer.flightCode && <span>✈️ {transfer.flightCode}</span>}
                      {transfer.returnTime && <span>↩ {transfer.returnTime.substring(0, 5)}</span>}
                      {transfer.passengerCount && <span>👥 {transfer.passengerCount}</span>}
                    </div>
                    {transfer.guests && transfer.guests.length > 0 && (
                      <div className="text-xs text-gray-600 mb-2">
                        {transfer.guests.map(g => g.name).join(', ')}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      {transfer.providerPrice ? (
                        <span className="text-sm font-bold text-green-600">{transfer.providerPrice} {currencyLabel(transfer.currency)}</span>
                      ) : (
                        <span className="text-xs text-orange-600 font-medium">⚠️ {t('transfers.notSet')}</span>
                      )}
                      {(transfer.providerDriverName || transfer.providerPlate) && (
                        <span className="text-xs text-gray-600">
                          {transfer.providerDriverName}{transfer.providerPlate ? ` · ${transfer.providerPlate}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.dateTime')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.transferType')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.route')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.passengerCount')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.guests')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.flightCode')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.returnTime')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.licensePlate')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.specialNotes')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('transfers.yourInfo')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transfers.map((transfer) => (
                    <tr
                      key={transfer.id}
                      onClick={() => {
                        setSelectedTransfer(transfer);
                        setIsDetailModalOpen(true);
                      }}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {format(new Date(transfer.transferDate), 'dd.MM.yyyy')}
                        </div>
                        <div className="text-gray-500">{transfer.transferTime.substring(0, 5)}</div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          transfer.tripType === 'arrival'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {getTripTypeLabel(transfer.tripType, t)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900">{transfer.pickupLocation}</div>
                        <div className="text-gray-500 text-xs">→ {transfer.dropoffLocation}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {transfer.passengerCount || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transfer.guests && transfer.guests.length > 0 ? (
                          transfer.guests.length <= 2 ? (
                            <div>
                              {transfer.guests.map((guest, idx) => (
                                <div key={idx} className="text-gray-900 text-xs">{guest.name}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-gray-600 text-xs">
                              {transfer.guests.length} {t('transfers.guestsCount')}
                            </div>
                          )
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transfer.flightCode || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {transfer.returnTime ? (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                            {transfer.returnTime.substring(0, 5)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transfer.providerPlate || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm max-w-xs">
                        {transfer.specialNotes ? (
                          <div className="text-gray-600 text-xs truncate" title={transfer.specialNotes}>
                            {transfer.specialNotes.length > 50
                              ? transfer.specialNotes.substring(0, 50) + '...'
                              : transfer.specialNotes}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transfer.status)}`}>
                          {transfer.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transfer.providerPrice ? (
                          <div>
                            <div className="font-bold text-green-600">{transfer.providerPrice} {currencyLabel(transfer.currency)}</div>
                            <div className="text-xs text-gray-600">{transfer.providerDriverName || t('transfers.driverNotSet')}</div>
                          </div>
                        ) : (
                          <span className="text-orange-600 font-medium">⚠️ {t('transfers.notSet')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>

        {/* Provider Edit Modal with Form */}
        {isDetailModalOpen && selectedTransfer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:p-4 z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-2xl w-full flex flex-col max-h-[95vh] sm:max-h-[90vh]">
              <div className="flex justify-between items-center px-4 py-3 sm:px-6 border-b border-gray-200 shrink-0">
                  <h2 className="text-lg font-bold text-gray-900">{t('transfers.transferDetails')}</h2>
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
              </div>
              <div className="overflow-y-auto flex-1 p-4 sm:p-6">

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.date')}</p>
                      <p className="font-medium">{format(new Date(selectedTransfer.transferDate), 'dd.MM.yyyy')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.time')}</p>
                      <p className="font-medium">{selectedTransfer.transferTime.substring(0, 5)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.tripType')}</p>
                      <p className="font-medium">{getTripTypeLabel(selectedTransfer.tripType, t)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('common.status')}</p>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTransfer.status)}`}>
                        {t(`transfers.statuses.${selectedTransfer.status === 'in_progress' ? 'inProgress' : selectedTransfer.status}`)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.pickupLocation')}</p>
                    <p className="font-medium">{selectedTransfer.pickupLocation}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('transfers.dropoffLocation')}</p>
                    <p className="font-medium">{selectedTransfer.dropoffLocation}</p>
                  </div>

                  {selectedTransfer.returnTime && (
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.returnTime')}</p>
                      <p className="font-medium">{selectedTransfer.returnTime.substring(0, 5)}</p>
                    </div>
                  )}

                  {selectedTransfer.flightCode && (
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.flightCode')}</p>
                      <p className="font-medium">{selectedTransfer.flightCode}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.vehicleType')}</p>
                      <p className="font-medium">{selectedTransfer.vehicleType || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.passengerCount')}</p>
                      <p className="font-medium">{selectedTransfer.passengerCount}</p>
                    </div>
                  </div>

                  {selectedTransfer.specialNotes && (
                    <div>
                      <p className="text-sm text-gray-500">{t('transfers.specialNotes')}</p>
                      <p className="font-medium whitespace-pre-wrap">{selectedTransfer.specialNotes}</p>
                    </div>
                  )}

                  {selectedTransfer.guests && selectedTransfer.guests.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">{t('transfers.guests')}</p>
                      <div className="space-y-2">
                        {selectedTransfer.guests.map((guest, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded">
                            <p className="font-medium">{guest.name}</p>
                            {guest.phone && <p className="text-sm text-gray-600">{guest.phone}</p>}
                            {guest.email && <p className="text-sm text-gray-600">{guest.email}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Provider Info - Prominent */}
                  <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-3">{t('transfers.yourInformation')}</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-blue-700">{t('transfers.providerPrice')}</p>
                        <p className="text-xl font-bold text-blue-900">
                          {selectedTransfer.providerPrice ? `${selectedTransfer.providerPrice} ${currencyLabel(selectedTransfer.currency)}` : t('transfers.notSet')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">{t('transfers.driverName')}</p>
                        <p className="font-semibold text-blue-900">
                          {selectedTransfer.providerDriverName || t('transfers.notSet')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">{t('transfers.driverPhone')}</p>
                        <p className="font-semibold text-blue-900">
                          {selectedTransfer.providerDriverPhone || t('transfers.notSet')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">{t('transfers.vehiclePlate')}</p>
                        <p className="font-semibold text-blue-900">
                          {selectedTransfer.providerPlate || t('transfers.notSet')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
              <div className="px-4 py-3 sm:px-6 border-t border-gray-200 shrink-0 flex gap-3">
                  <button
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      navigate(`/app/transfers/${selectedTransfer.id}`);
                    }}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
                  >
                    {t('transfers.editTransfer')}
                  </button>
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    {t('common.close')}
                  </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const ViewToggle = () => (
    <div className="flex items-center gap-1 bg-gray-200 dark:bg-gray-700 rounded-xl p-1">
      <button
        onClick={() => setViewMode('list')}
        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-semibold transition-all
          ${viewMode === 'list'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        Liste
      </button>
      <button
        onClick={() => setViewMode('calendar')}
        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-semibold transition-all
          ${viewMode === 'calendar'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Takvim
      </button>
    </div>
  );

  if (viewMode === 'calendar') {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('transfers.title')}</h1>
          <ViewToggle />
        </div>
        <CalendarPage embedded />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Title + View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('transfers.title')}</h1>
        </div>
        <ViewToggle />
      </div>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
          {canViewFinancials && (
            <button
              onClick={handleExportFinancialReport}
              className="inline-flex items-center px-3 py-2 sm:px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">{t('transfers.financialReport')}</span>
              <span className="sm:hidden">{t('transfers.report')}</span>
            </button>
          )}
          <button
            onClick={handleExportToExcel}
            className="inline-flex items-center px-3 py-2 sm:px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-200 transition-colors text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">{t('transfers.exportToExcel')}</span>
            <span className="sm:hidden">{t('transfers.excel')}</span>
          </button>
          {canBulkImport && (
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center px-3 py-2 sm:px-4 border border-green-300 dark:border-green-600 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 font-medium text-green-700 dark:text-green-400 transition-colors text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span className="hidden sm:inline">{t('transfers.bulkImport')}</span>
              <span className="sm:hidden">{t('transfers.import')}</span>
            </button>
          )}
          {canAddTransfer && (
            <button
              onClick={() => navigate('/app/transfers/new')}
              className="bg-blue-600 text-white px-3 py-2 sm:px-4 rounded-lg hover:bg-blue-700 font-medium text-sm sm:text-base"
            >
              + <span className="hidden sm:inline">{t('transfers.newTransfer')}</span><span className="sm:hidden">{t('transfers.new')}</span>
            </button>
          )}
      </div>

      {/* Quick View Toggle */}
      {(() => {
        const today = format(new Date(), 'yyyy-MM-dd');
        const yesterday = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
        const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
        const isDateOnly = (d: string) => filters.startDate === d && filters.endDate === d && !filters.status && !filters.activeOnly;
        const setDateFilter = (d: string) => {
          const { status, excludeStatus, activeOnly, startDate, endDate, ...rest } = filters;
          if (isDateOnly(d)) {
            setFilters({ ...rest, activeOnly: true });
          } else {
            setFilters({ ...rest, startDate: d, endDate: d });
          }
        };
        const btnBase = 'flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors border';
        const inactive = 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';
        return (
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            {/* Mobilde: satır 1 | Webde: düz sıranın parçası */}
            <div className="flex gap-2 sm:contents">
              <button
                onClick={() => { const { excludeStatus, activeOnly, ...rest } = filters; setFilters({ ...rest, status: TransferStatus.COMPLETED }); }}
                className={`${btnBase} ${filters.status === TransferStatus.COMPLETED ? 'bg-green-600 text-white border-green-600' : inactive}`}
              >
                ✓ {t('transfers.statuses.completed')}
              </button>
              <button
                onClick={() => { const { status, excludeStatus, activeOnly, ...rest } = filters; setFilters({ ...rest, activeOnly: true }); }}
                className={`${btnBase} ${filters.activeOnly && !filters.status ? 'bg-blue-600 text-white border-blue-600' : inactive}`}
              >
                ⏳ {t('transfers.activeTransfers')}
              </button>
            </div>
            {/* Mobilde: satır 2 | Webde: düz sıranın devamı */}
            <div className="flex gap-2 sm:contents">
              <button onClick={() => setDateFilter(yesterday)} className={`${btnBase} ${isDateOnly(yesterday) ? 'bg-gray-600 text-white border-gray-600' : inactive}`}>
                Dün
              </button>
              <button onClick={() => setDateFilter(today)} className={`${btnBase} ${isDateOnly(today) ? 'bg-orange-500 text-white border-orange-500' : inactive}`}>
                Bugün
              </button>
              <button onClick={() => setDateFilter(tomorrow)} className={`${btnBase} ${isDateOnly(tomorrow) ? 'bg-indigo-500 text-white border-indigo-500' : inactive}`}>
                Yarın
              </button>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
        {/* Search Bar + Mobile Filter Toggle */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('common.search')}
            </label>
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => {
                if (!e.target.value) setSearchAll(false);
                setFilters({ ...filters, search: e.target.value || undefined });
              }}
              placeholder="Müşteri adı, guest adı, uçuş kodu, alış veya bırakış noktası..."
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
            />
            {filters.search && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit">
                <div
                  onClick={() => setSearchAll(v => !v)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${searchAll ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${searchAll ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-300">Tüm transferlerde ara</span>
              </label>
            )}
          </div>
          {/* Mobile Filter Toggle Button */}
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="md:hidden flex items-center gap-1.5 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            {(() => {
              const count = [filters.status, filters.startDate, filters.endDate, filters.tripType, filters.assignedProviderId].filter(Boolean).length + (matchStatusFilter !== 'all' ? 1 : 0) + (assignmentFilter !== 'all' ? 1 : 0);
              return count > 0 ? (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">{count}</span>
              ) : null;
            })()}
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Filter Grid - collapsible on mobile */}
        <div className={`mt-4 ${showMobileFilters ? 'block' : 'hidden'} md:block`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('transfers.startDate')}
            </label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('transfers.endDate')}
            </label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as TransferStatus || undefined })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('transfers.allStatus')}</option>
              <option value={TransferStatus.AVAILABLE}>{t('transfers.statuses.available')}</option>
              <option value={TransferStatus.CONFIRMED}>{t('transfers.statuses.confirmed')}</option>
              <option value={TransferStatus.IN_PROGRESS}>{t('transfers.statuses.inProgress')}</option>
              <option value={TransferStatus.COMPLETED}>{t('transfers.statuses.completed')}</option>
              <option value={TransferStatus.CANCELLED}>{t('transfers.statuses.cancelled')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('transfers.tripType')}
            </label>
            <select
              value={filters.tripType || ''}
              onChange={(e) => setFilters({ ...filters, tripType: e.target.value as TripType || undefined })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('transfers.allTypes')}</option>
              <option value={TripType.ARRIVAL}>{t('transfers.arrival')}</option>
              <option value={TripType.DEPARTURE}>{t('transfers.departure')}</option>
            </select>
          </div>

          {/* Assignment Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('transfers.assignedTo')}
            </label>
            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value as 'all' | 'assigned' | 'unassigned')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="all">{t('common.all')}</option>
              <option value="assigned">{t('transfers.assigned')}</option>
              <option value="unassigned">{t('transfers.unassigned')}</option>
            </select>
          </div>

          {/* Match Status Filter */}
          {canViewFinancials && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                {t('transfers.matchStatus')}
              </label>
              <select
                value={matchStatusFilter}
                onChange={(e) => setMatchStatusFilter(e.target.value as 'all' | 'matched' | 'unmatched')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="all">{t('transfers.matchStatusAll')}</option>
                <option value="matched">⇄ {t('transfers.matchStatusMatched')}</option>
                <option value="unmatched">{t('transfers.matchStatusUnmatched')}</option>
              </select>
            </div>
          )}

          {canViewFinancials && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t('transfers.provider')}
                </label>
                <select
                  value={filters.assignedProviderId || ''}
                  onChange={(e) => setFilters({ ...filters, assignedProviderId: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('transfers.allProviders')}</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Clear Filters Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              setFilters(getDefaultFilters());
              setMatchStatusFilter('all');
              setAssignmentFilter('all');
            }}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-200 transition-colors"
          >
            {t('transfers.clearAllFilters')}
          </button>
        </div>
        </div>{/* End collapsible filter section */}

        {/* Active Filters Badges */}
        {(hasNonDefaultFilters(filters) || matchStatusFilter !== 'all' || assignmentFilter !== 'all') && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700">{t('transfers.activeFilters')}:</span>
            {filters.search && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {t('common.search')}: "{filters.search}"
                <button
                  onClick={() => setFilters({ ...filters, search: undefined })}
                  className="ml-2 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {t('common.status')}: {t(`transfers.statuses.${filters.status === 'in_progress' ? 'inProgress' : filters.status}`)}
                <button
                  onClick={() => setFilters({ ...filters, status: undefined })}
                  className="ml-2 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.tripType && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {t('transfers.tripType')}: {getTripTypeLabel(filters.tripType, t)}
                <button
                  onClick={() => setFilters({ ...filters, tripType: undefined })}
                  className="ml-2 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            )}
            {filters.vehicleType && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {t('transfers.vehicleType')}: {filters.vehicleType}
                <button
                  onClick={() => setFilters({ ...filters, vehicleType: undefined })}
                  className="ml-2 hover:text-blue-900"
                >
                  ×
                </button>
              </span>
            )}
            {matchStatusFilter !== 'all' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-800">
                {t('transfers.matchStatus')}: {matchStatusFilter === 'matched' ? `⇄ ${t('transfers.matchStatusMatched')}` : t('transfers.matchStatusUnmatched')}
                <button
                  onClick={() => setMatchStatusFilter('all')}
                  className="ml-2 hover:text-amber-900"
                >
                  ×
                </button>
              </span>
            )}
            {assignmentFilter !== 'all' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                {t('transfers.assignedTo')}: {assignmentFilter === 'assigned' ? t('transfers.assigned') : t('transfers.unassigned')}
                <button
                  onClick={() => setAssignmentFilter('all')}
                  className="ml-2 hover:text-purple-900"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Transfer Count */}
      {!loading && sortedTransfers.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-200">{nonCancelledCount}</span>
          <span>transfer</span>
          {cancelledCount > 0 && (
            <span className="text-gray-400 dark:text-gray-500">({cancelledCount} iptal hariç)</span>
          )}
        </div>
      )}

      {/* Transfers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-600">{t('common.loading')}</div>
        ) : transfers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>{t('transfers.noTransfersFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {sortedTransfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className={`p-4 cursor-pointer transition-colors ${
                    transfer.status === 'cancelled'
                      ? 'border-l-4 border-red-500 bg-red-50 hover:bg-red-100'
                      : 'hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    setSelectedTransfer(transfer);
                    setIsDetailModalOpen(true);
                  }}
                >
                  {/* Header: Code, Status, Match Icon */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">
                        {transfer.transferCode || '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        transfer.tripType === 'arrival' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {getTripTypeLabel(transfer.tripType, t)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transfer.status)}`}>
                        {t(`transfers.statuses.${transfer.status === 'in_progress' ? 'inProgress' : transfer.status}`)}
                      </span>
                    </div>
                  </div>

                  {/* Passenger Info */}
                  <div className="mb-2">
                    <p className="font-medium text-gray-900">{transfer.customerName}</p>
                    <p className="text-sm text-gray-500">{transfer.customerPhone}</p>
                  </div>

                  {/* Date & Time */}
                  <div className="flex items-center gap-4 text-sm mb-2">
                    <span className="text-gray-600">
                      📅 {format(new Date(transfer.transferDate), 'dd.MM.yyyy')}
                    </span>
                    <span className="text-gray-600">
                      🕐 {transfer.transferTime.substring(0, 5)}
                    </span>
                    {transfer.flightCode && (
                      <span className="text-gray-600">✈️ {transfer.flightCode}</span>
                    )}
                  </div>

                  {/* Route */}
                  <div className="text-sm mb-2">
                    <span className="text-gray-900">{transfer.pickupLocation}</span>
                    <span className="text-gray-500 mx-2">→</span>
                    <span className="text-gray-900">{transfer.dropoffLocation}</span>
                  </div>

                  {/* Assigned & Plate */}
                  <div className="flex flex-wrap gap-2 text-sm mb-2">
                    {transfer.providerDriverName && (
                      <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                        👤 {transfer.providerDriverName}
                      </span>
                    )}
                    {transfer.providerPlate && (
                      <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                        🚗 {transfer.providerPlate}
                      </span>
                    )}
                    {!transfer.providerDriverName && transfer.assignedDriver?.fullName && (
                      <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                        👤 {transfer.assignedDriver.fullName}
                      </span>
                    )}
                  </div>

                  {/* Financial Info (if allowed) */}
                  {canViewFinancials && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        {transfer.providerPrice && (
                          <span className="text-sm font-medium text-gray-900">
                            {transfer.providerPrice} {currencyLabel(transfer.currency)}
                          </span>
                        )}
                        {transfer.claimedByPoolMember ? (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transfer.claimedByPoolMember.poolMemberPaid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {transfer.claimedByPoolMember.poolMemberPaid ? `✓ ${t('transfers.paid')}` : t('transfers.unpaid')}
                          </span>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transfer.providerPaid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {transfer.providerPaid ? `✓ ${t('transfers.paid')}` : t('transfers.unpaid')}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/transfers/${transfer.id}`);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {canEditTransfer ? t('common.edit') : t('common.view')}
                      </button>
                    </div>
                  )}
                  {!canViewFinancials && (
                    <div className="flex justify-end pt-2 border-t border-gray-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/transfers/${transfer.id}`);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {canEditTransfer ? t('common.edit') : t('common.view')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('transfers.id')}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer')}
                  >
                    <div className="flex items-center">
                      {t('transfers.passenger')}
                      {sortBy === 'customer' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center">
                      {t('transfers.dateTime')}
                      {sortBy === 'date' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('transfers.transferType')}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('pickup')}
                  >
                    <div className="flex items-center">
                      {t('transfers.route')}
                      {sortBy === 'pickup' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('transfers.mainGuest')}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('plate')}
                  >
                    <div className="flex items-center">
                      {t('transfers.licensePlate')}
                      {sortBy === 'plate' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('assigned')}
                  >
                    <div className="flex items-center">
                      {t('transfers.assignedTo')}
                      {sortBy === 'assigned' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  {canViewFinancials && (
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('price')}
                    >
                      <div className="flex items-center">
                        {t('transfers.providerPrice')}
                        {sortBy === 'price' && (
                          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  )}
                  {canViewFinancials && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('transfers.paymentStatus')}
                    </th>
                  )}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      {t('common.status')}
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('transfers.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedTransfers.map((transfer) => (
                  <tr
                    key={transfer.id}
                    className={`cursor-pointer transition-colors ${
                      transfer.status === 'cancelled'
                        ? 'border-l-4 border-red-500 bg-red-50 hover:bg-red-100'
                        : 'hover:bg-blue-50'
                    }`}
                    onClick={() => {
                      setSelectedTransfer(transfer);
                      setIsDetailModalOpen(true);
                    }}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">
                          {transfer.transferCode || '-'}
                        </span>
                        {false && (
                          <button
                            className="hidden"
                          >
                            <span></span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium text-gray-900">{transfer.customerName}</div>
                      <div className="text-gray-500">{transfer.customerPhone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900">
                        {format(new Date(transfer.transferDate), 'dd.MM.yyyy')}
                      </div>
                      <div className="text-gray-500">{transfer.transferTime.substring(0, 5)}</div>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        transfer.tripType === 'arrival'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {getTripTypeLabel(transfer.tripType, t)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-gray-900">{transfer.pickupLocation}</div>
                      <div className="text-gray-500">→ {transfer.dropoffLocation}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-[160px]">
                      <div className="truncate" title={transfer.guests?.[0]?.name}>
                        {transfer.guests && transfer.guests.length > 0 ? transfer.guests[0].name : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transfer.providerPlate || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {transfer.providerDriverName ? (
                        <>
                          <div className="font-medium text-gray-900">{transfer.providerDriverName}</div>
                          {transfer.providerDriverPhone && (
                            <div className="text-gray-500">{transfer.providerDriverPhone}</div>
                          )}
                          {transfer.assignedProvider && canViewFinancials && (
                            <div className="text-xs text-gray-400 mt-1">{t('transfers.via')} {transfer.assignedProvider.name}</div>
                          )}
                        </>
                      ) : transfer.claimedByPoolMember ? (
                        <>
                          <div className="font-medium text-gray-900">{transfer.claimedByPoolMember.fullName}</div>
                          {transfer.claimedByPoolMember.phone && (
                            <div className="text-gray-500">{transfer.claimedByPoolMember.phone}</div>
                          )}
                          <div className="text-xs text-purple-500 mt-0.5">Pool</div>
                        </>
                      ) : (
                        <div className="text-gray-900">
                          {transfer.assignedDriver?.fullName ||
                           (canViewFinancials && transfer.assignedProvider?.name) ||
                           transfer.assignedOrganization?.name ||
                           '-'}
                        </div>
                      )}
                    </td>
                    {canViewFinancials && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {transfer.providerPrice ? `${transfer.providerPrice} ${currencyLabel(transfer.currency)}` : '-'}
                      </td>
                    )}
                    {canViewFinancials && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transfer.claimedByPoolMember ? (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transfer.claimedByPoolMember.poolMemberPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {transfer.claimedByPoolMember.poolMemberPaid ? `✓ ${t('transfers.paid')}` : t('transfers.unpaid')}
                          </span>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transfer.providerPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {transfer.providerPaid ? `✓ ${t('transfers.paid')}` : t('transfers.unpaid')}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          transfer.status
                        )}`}
                      >
                        {t(`transfers.statuses.${transfer.status === 'in_progress' ? 'inProgress' : transfer.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                      {canEditTransfer && (
                        <button
                          onClick={() => navigate(`/app/transfers/${transfer.id}`)}
                          className="text-blue-600 hover:underline"
                        >
                          {t('common.edit')}
                        </button>
                      )}
                      {!canEditTransfer && (
                        <button
                          onClick={() => navigate(`/app/transfers/${transfer.id}`)}
                          className="text-gray-600 hover:underline"
                        >
                          {t('common.view')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      {/* Transfer Detail Modal for Admin/Staff */}
      {isDetailModalOpen && selectedTransfer && (
        <TransferDetailModal
          transfer={selectedTransfer}
          onClose={() => setIsDetailModalOpen(false)}
          onEdit={canEditTransfer ? () => {
            setIsDetailModalOpen(false);
            navigate(`/app/transfers/${selectedTransfer.id}`);
          } : undefined}
          onRepeat={canAddTransfer ? () => {
            setIsDetailModalOpen(false);
            navigate('/app/transfers/new', { state: { repeatFrom: selectedTransfer } });
          } : undefined}
          onComplete={canEditTransfer ? async () => {
            try {
              await transfersService.update(selectedTransfer.id, { status: 'completed' } as any);
              toast.success('Transfer tamamlandı.');
              setIsDetailModalOpen(false);
              loadTransfers();
            } catch (err: any) {
              toast.error(err?.response?.data?.message || 'Transfer tamamlanamadı.');
            }
          } : undefined}
          canViewFinancials={canViewFinancials}
          canViewHistory={user?.role !== UserRole.STAFF}
        />
      )}

      {/* Excel Import Modal */}
      <ExcelImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => loadTransfers()}
      />
    </div>
  );
};

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getTripTypeLabel(tripType: string | undefined, t?: (key: string) => string): string {
  if (t) {
    const labels: Record<string, string> = {
      arrival: t('transfers.arrival'),
      departure: t('transfers.departure'),
    };
    return labels[tripType || ''] || '-';
  }
  const labels: Record<string, string> = {
    arrival: 'Arrival',
    departure: 'Departure',
  };
  return labels[tripType || ''] || '-';
}
